import { useCallback, useEffect, useMemo, useState } from 'react';
import LinkableTextArea from './LinkableTextArea.jsx';
import RecordDetailPopup from './RecordDetailPopup.jsx';
import {
  getActors,
  getCauses,
  getEmployees,
  getImpacts,
  getLargeCategories,
  getMiddleCategories,
  getSmallCategories,
  getOems,
  getOemRegions,
  getRegions,
  getRisks,
  getStatuses,
  deleteProblem,
  updateProblem,
} from '../api/dashboardApi.js';
import {
  buildOptionsByOemRegion,
  buildSmallCategoryOptionsByMiddleId,
  buildSmallCategoryLookupByMiddleId,
} from '../utils/converters.js';
import { canEdit } from '../../../auth/permissions.js';
import '../styles/input-controls.css';

const BASE_COLUMNS = [
  { key: 'deadlineCheck', label: '期限チェック', type: 'text', readOnly: true },
  { key: 'oemRegion', label: 'OEM×対地', type: 'select', required: true },
  { key: 'noId', label: 'ID', type: 'text', readOnly: true },
  { key: 'summary', label: 'タイトル', type: 'text', required: true },
  { key: 'dueDate', label: '対応期限', type: 'date', required: true },
  { key: 'status', label: 'ステータス', type: 'select', required: true },
  { key: 'pmoConfirmCompletedAt', label: 'PMO確認完了日', type: 'date' },
  { key: 'todo', label: 'ToDo', type: 'checkbox' },
  { key: 'projectIssue', label: 'PJ全体課題', type: 'checkbox' },
  { key: 'largeCategory', label: '大項目', type: 'select' },
  { key: 'middleCategory', label: '中項目', type: 'select' },
  { key: 'smallItem', label: '小項目', type: 'select' },
  { key: 'issueOrigin', label: '課題起因', type: 'select', required: true },
  { key: 'ownerRegion', label: '領域', type: 'select', required: true },
  { key: 'owner', label: '担当者', type: 'tags', required: true },
  { key: 'impact', label: '影響度', type: 'select', required: true },
  { key: 'closeCondition', label: 'クローズ条件', type: 'text', required: true },
  { key: 'policyDecisionDate', label: '対応方針決定日', type: 'date' },
  { key: 'policyDetail', label: '対応方針詳細', type: 'text' },
  { key: 'ownerAreaCompletedAt', label: '担当領域完了日', type: 'date' },
  { key: 'riskEmergenceIssue', label: 'リスク顕在化課題', type: 'checkbox' },
  { key: 'riskManagement', label: '関連リスク', type: 'tags' },
  { key: 'issueByRiskEmergence', label: 'リスク顕在化による課題移行日', type: 'date' },
  { key: 'scheduleIssue', label: 'スケジュール課題', type: 'checkbox' },
  { key: 'createdBy', label: '作成者', type: 'text', readOnly: true },
  { key: 'createdAt', label: '作成日時', type: 'text', readOnly: true },
  { key: 'updatedBy', label: '最終更新者', type: 'text', readOnly: true },
  { key: 'updatedAt', label: '最終更新日時', type: 'text', readOnly: true },
];

/* ---------- ヘルパー ---------- */

function buildLookupMap(items, key, idKey = 'id') {
  const map = new Map();
  items.forEach((item) => {
    if (item[key] !== undefined && item[key] !== null && item[key] !== '') {
      map.set(item[key], item[idKey]);
    }
  });
  return map;
}

function buildMiddleCategoryOptionsByLarge(items) {
  const map = new Map();
  items.forEach((item) => {
    const largeName = item.large_category?.name;
    const middleName = item.middle_category;
    if (!largeName || !middleName) return;
    if (!map.has(largeName)) map.set(largeName, new Set());
    map.get(largeName).add(middleName);
  });
  return Object.fromEntries(
    Array.from(map.entries()).map(([large, middles]) => [large, ['', ...Array.from(middles)]]),
  );
}

// 大項目名→ID
function buildLargeCategoryLookup(items) {
  const map = new Map();
  items.forEach((item) => {
    if (item.large_category && item.id) map.set(item.large_category, item.id);
  });
  return map;
}

// (大項目::中項目)→ID。中項目名は大項目内で一意なので大項目込みのキーで解決する。
function buildMiddleCategoryLookup(items) {
  const map = new Map();
  items.forEach((item) => {
    const largeName = item.large_category?.name;
    const middleName = item.middle_category;
    if (largeName && middleName && item.id) {
      map.set(`${largeName}::${middleName}`, item.id);
    }
  });
  return map;
}

function resolveEmployeeIds(displayValue, employeeByName) {
  /* istanbul ignore if -- 担当者は必須項目でマスタも保存時点で必ずロード済み */
  if (!displayValue || !employeeByName) return [];
  return displayValue
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
    .map((name) => employeeByName.get(name))
    .filter(Boolean);
}

function ensureValueInOptions(opts, value) {
  if (!value || opts.includes(value)) return opts;
  return [...opts, value];
}

// master ロード後に1カラム分の選択肢を解決する。
// row(=initialRow)を優先し、カスケード項目は編集中の値(editingSnapshot)も参照する。
// select 以外・特定キー以外のカラムはそのまま返す。
export function resolveProblemColumnOptions(col, master, row, editingSnapshot) {
  const { options } = master;
  let colOptions;
  if (col.key === 'oemRegion') colOptions = options.oemRegion;
  else if (col.key === 'status') colOptions = options.status;
  else if (col.key === 'ownerRegion') colOptions = options.ownerRegion;
  else if (col.key === 'owner') colOptions = options.owner;
  else if (col.key === 'issueOrigin') colOptions = options.issueOrigin;
  else if (col.key === 'impact') colOptions = options.impact;
  else if (col.key === 'riskManagement') {
    const oemKey = (editingSnapshot?.oemRegion || row?.oemRegion) || '';
    colOptions = options.riskManagementByOemRegion?.[oemKey] || [''];
  }
  else if (col.key === 'largeCategory') colOptions = options.largeCategory;
  else if (col.key === 'middleCategory') {
    // 編集中に選び直した大項目を優先（初期値ではなく現在の選択に連動させる）
    const largeVal = editingSnapshot?.largeCategory || row?.largeCategory;
    colOptions = master.middleCategoryOptionsByLarge?.[largeVal] || [''];
  } else if (col.key === 'smallItem') {
    // 同名中項目の衝突を避けるため、大項目込みで中項目IDを解決してから小項目候補を引く。
    // こちらも編集中の選択（大・中）を優先する。
    const largeVal = editingSnapshot?.largeCategory || row?.largeCategory;
    const middleVal = editingSnapshot?.middleCategory || row?.middleCategory;
    const middleId = master.refs?.middleByLargePair?.get(`${largeVal || ''}::${middleVal || ''}`);
    colOptions = (middleId != null && master.smallCategoryOptionsByMiddleId?.[middleId]) || [''];
  } else if (col.type === 'select') {
    colOptions = options.selectByKey[col.key] || [''];
  } else {
    return col;
  }

  // 現在値の担保は編集中の値を優先（大項目変更でリセットされた中/小の残留を防ぐ）
  const currentValue = editingSnapshot && Object.prototype.hasOwnProperty.call(editingSnapshot, col.key)
    ? editingSnapshot[col.key]
    : row?.[col.key];
  colOptions = ensureValueInOptions(colOptions, currentValue);
  return { ...col, options: colOptions };
}

function toPatchPayload(row, refs) {
  const payload = {};

  /* istanbul ignore else -- タイトルは必須項目なので保存時点で必ず入っている */
  if (row.summary) payload.title = row.summary;

  if (row.oemRegion && refs.oemRegionLookup?.has(row.oemRegion)) {
    const resolved = refs.oemRegionLookup.get(row.oemRegion);
    payload.oem_id = resolved.oemId;
    payload.region_id = resolved.regionId;
  }

  const statusId = refs.statusByName?.get(row.status);
  if (statusId) payload.status_id = statusId;

  const actorId = refs.actorByName?.get(row.ownerRegion);
  if (actorId) payload.actor_id = actorId;

  const causeId = refs.causeByName?.get(row.issueOrigin);
  if (causeId) payload.cause_id = causeId;

  const impactId = refs.impactByName?.get(row.impact);
  if (impactId) payload.impact_id = impactId;

  // 大中小項目は課題自身のカテゴリとして送る（進捗リンクは進捗管理詳細でのみ管理）。
  // 大項目のみ・大中のみの登録も許可するため、解決できない階層は null にする。
  // 小項目は同名中項目の衝突を避けるため、大項目込みで解決した中項目IDでスコープする。
  /* istanbul ignore next -- 大中小は選択式でマスタ由来のため、保存時点では必ず解決できる */
  const middleCategoryId = row.middleCategory
    ? (refs.middleByLargePair?.get(`${row.largeCategory || ''}::${row.middleCategory}`) || null)
    : null;
  /* istanbul ignore next -- 同上 */
  payload.large_category_id = refs.largeByName?.get(row.largeCategory) || null;
  payload.middle_category_id = middleCategoryId;
  /* istanbul ignore next -- 同上 */
  payload.small_category_id = (row.smallItem && middleCategoryId != null)
    ? (refs.smallByMiddleIdPair?.get(`${middleCategoryId}::${row.smallItem}`) || null)
    : null;

  payload.is_all_problem = Boolean(row.projectIssue);
  /* istanbul ignore next -- 対応期限は必須項目なので空にならない */
  payload.deadline = row.dueDate || null;

  const employeeIds = resolveEmployeeIds(row.owner, refs.employeeByName);
  payload.employee_ids = employeeIds;

  const riskIds = (row.riskManagement || '').split(',').map((s) => s.trim()).filter(Boolean)
    .map((name) => refs.riskByName?.get(name)).filter(Boolean);
  payload.risk_ids = riskIds;

  payload.pmo_confirm_completed_at = row.pmoConfirmCompletedAt || null;
  payload.owner_area_completed_at = row.ownerAreaCompletedAt || null;
  payload.is_risk_emergence = Boolean(row.riskEmergenceIssue);
  payload.issue_by_risk_emergence = row.issueByRiskEmergence || null;
  payload.is_schedule_issue = Boolean(row.scheduleIssue);
  /* istanbul ignore next -- クローズ条件は必須項目なので空にならない */
  payload.close_condition = row.closeCondition || '';
  payload.policy_decision_date = row.policyDecisionDate || null;
  /* istanbul ignore next -- 未入力時は空文字が入るため右辺には落ちない */
  payload.policy_detail = row.policyDetail || '';
  payload.todo = Boolean(row.todo);

  // 楽観ロック用タイムスタンプ（ISO）を送信
  if (row.updated_at_iso) {
    payload.updated_at_iso = row.updated_at_iso;
  }

  return payload;
}

/* ---------- コンポーネント ---------- */

export default function ProblemRecordEditor({ open, initialRow, onClose, onSaved, onDeleted, readOnly: readOnlyProp }) {
  const readOnly = readOnlyProp !== undefined ? readOnlyProp : !canEdit('ダッシュボード');
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [master, setMaster] = useState(null);
  const [masterLoading, setMasterLoading] = useState(false);

  useEffect(() => {
    if (!open || master) return;
    let active = true;
    setMasterLoading(true);

    Promise.all([
      getStatuses(),
      getActors(),
      getCauses(),
      getImpacts(),
      getOems(),
      getRegions(),
      getOemRegions(),
      getRisks(),
      getLargeCategories(),
      getMiddleCategories(),
      getSmallCategories(),
      getEmployees(),
    ])
      .then(([
        statuses, actors, causes, impacts,
        _oems, _regions, oemRegions,
        risks, largeCategories, middleCategories,
        smallCategories, employees,
      ]) => {
        if (!active) return;

        const statusByName = buildLookupMap(statuses, 'status');
        const actorByName = buildLookupMap(actors, 'actor');
        const causeByName = buildLookupMap(causes, 'cause');
        const impactByName = buildLookupMap(impacts, 'impact');
        const employeeByName = buildLookupMap(employees, 'user_name', 'employee_id');
        const riskByName = new Map();
        risks.forEach((r) => {
          const name = r.title || r.riskManagement;
          if (name && r.id) riskByName.set(name, r.id);
        });

        const oemRegionOptions = [''];
        const oemRegionLookup = new Map();
        oemRegions.forEach((item) => {
          const oemName = item.oem?.name || '';
          const regionName = item.region?.name || '';
          if (oemName && regionName) {
            const label = `${oemName}×${regionName}`;
            if (!oemRegionLookup.has(label)) {
              oemRegionOptions.push(label);
              oemRegionLookup.set(label, {
                oemId: item.oem.id,
                regionId: item.region.id,
              });
            }
          }
        });

        const statusOptions = ['', ...statuses.map((i) => i.status).filter(Boolean)];
        const actorOptions = ['', ...actors.map((i) => i.actor).filter(Boolean)];
        const causeOptions = ['', ...causes.map((i) => i.cause).filter(Boolean)];
        const impactOptions = ['', ...impacts.map((i) => i.impact).filter(Boolean)];
        const employeeOptions = ['', ...employees.map((i) => i.user_name).filter(Boolean)];
        // 関連リスクの候補は行のOEM×対地に限定する（別OEM×対地への誤リンク防止）
        const riskManagementByOemRegion = buildOptionsByOemRegion(risks, [
          (i) => i.title || i.riskManagement,
        ]);
        const largeCategoryOpts = [
          '',
          ...Array.from(new Set(largeCategories.map((i) => i.large_category).filter(Boolean))),
        ];
        const middleCategoryOptionsByLargeMap = buildMiddleCategoryOptionsByLarge(middleCategories);
        const smallCategoryOptionsByMiddleIdMap = buildSmallCategoryOptionsByMiddleId(smallCategories);

        // カテゴリ名→ID のルックアップ（課題自身のカテゴリFKを解決するため）
        const largeByName = buildLargeCategoryLookup(largeCategories);
        const middleByLargePair = buildMiddleCategoryLookup(middleCategories);
        const smallByMiddleIdPair = buildSmallCategoryLookupByMiddleId(smallCategories);

        setMaster({
          refs: {
            statusByName,
            actorByName,
            causeByName,
            impactByName,
            employeeByName,
            riskByName,
            oemRegionLookup,
            largeByName,
            middleByLargePair,
            smallByMiddleIdPair,
          },
          options: {
            oemRegion: oemRegionOptions,
            status: statusOptions,
            ownerRegion: actorOptions,
            owner: employeeOptions,
            issueOrigin: causeOptions,
            impact: impactOptions,
            riskManagementByOemRegion,
            largeCategory: largeCategoryOpts,
            selectByKey: {},
          },
          middleCategoryOptionsByLarge: middleCategoryOptionsByLargeMap,
          smallCategoryOptionsByMiddleId: smallCategoryOptionsByMiddleIdMap,
        });
      })
      .catch(() => {})
      .finally(() => {
        if (active) setMasterLoading(false);
      });

    return () => { active = false; };
  }, [open, master]);

  useEffect(() => {
    if (initialRow) {
      setEditingRow({
        ...initialRow,
        projectIssue: Boolean(initialRow.projectIssue),
        riskEmergenceIssue: Boolean(initialRow.riskEmergenceIssue),
        scheduleIssue: Boolean(initialRow.scheduleIssue),
      });
    } else {
      setEditingRow(null);
    }
  }, [initialRow]);

  const columns = useMemo(() => {
    const row = initialRow;
    if (!master) {
      return BASE_COLUMNS.map((col) => {
        if (col.type !== 'select') return col;
        const value = row?.[col.key];
        const opts = value ? ['', value] : [''];
        return { ...col, options: opts };
      });
    }
    const editingSnapshot = {
      largeCategory: editingRow?.largeCategory,
      middleCategory: editingRow?.middleCategory,
      smallItem: editingRow?.smallItem,
      oemRegion: editingRow?.oemRegion,
    };
    return BASE_COLUMNS.map((col) => resolveProblemColumnOptions(col, master, row, editingSnapshot));
  }, [master, initialRow, editingRow?.largeCategory, editingRow?.middleCategory, editingRow?.smallItem, editingRow?.oemRegion]);

  const updateField = useCallback((key, value) => {
    setEditingRow((prev) => {
      /* istanbul ignore if -- 編集中のみ呼ばれるため prev は常に存在する */
      if (!prev) return prev;
      const next = { ...prev, [key]: value };
      if (key === 'largeCategory') {
        const middleOpts = master?.middleCategoryOptionsByLarge?.[value] || [''];
        if (!middleOpts.includes(next.middleCategory || '')) {
          next.middleCategory = '';
          next.smallItem = '';
        } else {
          // 中項目名は残せても、大項目が変わると中項目IDが変わり小項目候補も変わる
          const middleId = master?.refs?.middleByLargePair?.get(`${value || ''}::${next.middleCategory || ''}`);
          const smallOpts = (middleId != null && master?.smallCategoryOptionsByMiddleId?.[middleId]) || [''];
          if (!smallOpts.includes(next.smallItem || '')) {
            next.smallItem = '';
          }
        }
      }
      if (key === 'middleCategory') {
        const middleId = master?.refs?.middleByLargePair?.get(`${next.largeCategory || ''}::${value || ''}`);
        const smallOpts = (middleId != null && master?.smallCategoryOptionsByMiddleId?.[middleId]) || [''];
        if (!smallOpts.includes(next.smallItem || '')) {
          next.smallItem = '';
        }
      }
      return next;
    });
  }, [master]);

  const renderField = useCallback((row, column) => {
    if (column.type === 'tags') {
      
      const opts = column.options || [''];
      const raw = row[column.key] || '';
      const filled = raw.split(',').map((s) => s.trim()).filter(Boolean);
      const hasNewSlot = raw.trimEnd().endsWith(',');
      const items = hasNewSlot ? [...filled, ''] : filled;

      const addItem = () => {
        const current = row[column.key] || '';
        const trimmed = current.replace(/,\s*$/, '');
        updateField(column.key, trimmed ? trimmed + ',' : ',');
      };

      if (items.length === 0) {
        return (
          <button
            type="button"
            onClick={addItem}
            style={{
              border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
              padding: '4px 12px', fontSize: '13px', color: '#6b7280', cursor: 'pointer',
            }}
          >
            ＋ 追加
          </button>
        );
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {items.map((item, i) => {
            const selectOpts = opts.includes(item) ? opts : [...opts, item];
            return (
              <div key={`${column.key}-${i}`} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <select
                  className="db-select-control"
                  value={item}
                  onChange={(e) => {
                    const newItems = [...items];
                    newItems[i] = e.target.value;
                    updateField(column.key, newItems.filter(Boolean).join(', '));
                  }}
                  style={{ flex: 1 }}
                >
                  {selectOpts.map((opt) => (
                    <option key={opt || 'empty'} value={opt}>
                      {opt || '選択してください'}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    const newItems = items.filter((_, idx) => idx !== i);
                    updateField(column.key, newItems.join(', '));
                  }}
                  style={{
                    border: 'none', background: 'transparent', color: '#ef4444',
                    fontSize: '16px', cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
                  }}
                  title="削除"
                >
                  ×
                </button>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addItem}
            style={{
              border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
              padding: '3px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            ＋ 追加
          </button>
        </div>
      );
    }

    if (column.type === 'select') {
      /* istanbul ignore next -- この select 列は列定義側で options を必ず設定している */
      const opts = column.options || [''];
      return (
        <select
          className="db-select-control"
          onChange={(e) => updateField(column.key, e.target.value)}
          value={row[column.key] || ''}
        >
          {opts.map((option) => (
            <option key={option || 'empty'} value={option}>
              {option || '選択してください'}
            </option>
          ))}
        </select>
      );
    }

    if (column.type === 'checkbox') {
      return (
        <input
          checked={Boolean(row[column.key])}
          onChange={(e) => updateField(column.key, e.target.checked)}
          type="checkbox"
        />
      );
    }

    if (column.type === 'date') {
      return (
        <input
          className="db-input-control"
          max="9999-12-31"
          onChange={(e) => updateField(column.key, e.target.value)}
          type="date"
          value={row[column.key] || ''}
        />
      );
    }

    if (['policyDetail', 'closeCondition'].includes(column.key)) {
      return (
        <LinkableTextArea
          onChange={(next) => updateField(column.key, next)}
          value={row[column.key]}
        />
      );
    }

    return (
      <input
        className="db-input-control"
        onChange={(e) => updateField(column.key, e.target.value)}
        type="text"
        value={row[column.key] || ''}
      />
    );
  }, [master, updateField]);

  const formatValue = useCallback((column, value) => {
    if (column.type === 'checkbox') return value ? 'はい' : 'いいえ';
    return value || '—';
  }, []);

  const handleDelete = useCallback(async (row) => {
    /* istanbul ignore if -- 削除ボタンは既存レコード（id あり）にしか出ない */
    if (!row?.id) return;
    setDeleting(true);
    setSaveError('');
    try {
      await deleteProblem(row.id, row.updated_at_iso ?? null);
      setEditingRow(null);
      onDeleted?.();
      onClose?.();
    } catch (err) {
      console.error('[ProblemRecordEditor] Delete error:', err);
      setSaveError(err.message || '削除に失敗しました');
    } finally {
      setDeleting(false);
    }
  }, [onDeleted, onClose]);

  const handleSave = useCallback(async () => {
    if (!editingRow?.id) {
      setSaveError('レコードIDが見つかりません。ポップアップを閉じて再度開いてください。');
      return;
    }

    const missingFields = columns
      .filter((c) => c.required && !c.readOnly)
      .filter((c) => {
        const val = editingRow[c.key];
        /* istanbul ignore if -- 必須列はすべて文字列項目のため配列にならない */
        if (Array.isArray(val)) return val.length === 0;
        /* istanbul ignore if -- 必須列にチェックボックスは無い */
        if (typeof val === 'boolean') return false;
        return !val && val !== 0;
      })
      .map((c) => c.label);
    if (missingFields.length > 0) {
      setSaveError(`以下の必須項目が未入力です：\n${missingFields.join('、')}`);
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      /* istanbul ignore next -- マスタ未ロード中は保存ボタンが無効なのでここへ来ない */
      const refs = master?.refs || {};
      const payload = toPatchPayload(editingRow, refs);
      await updateProblem(editingRow.id, payload);
      setEditingRow(null);
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('[ProblemRecordEditor] Save error:', err);
      if (err.status === 409) {
        setSaveError('他のユーザーによってこのレコードが更新されました。\n入力した内容は画面に残っています。\nOK を押した後、メモなど必要な内容をコピーしてから\nキャンセルで閉じて最新データを確認してください。');
      } else {
        setSaveError(err.message || '保存に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  }, [editingRow, master, columns, onSaved, onClose]);

  const handleClose = useCallback(() => {
    setSaveError('');
    onClose?.();
  }, [onClose]);

  return (
    <>
      <RecordDetailPopup
        columns={columns}
        deleting={deleting}
        formatValue={formatValue}
        onClose={handleClose}
        onDelete={readOnly ? undefined : handleDelete}
        onSave={readOnly ? undefined : handleSave}
        open={open && Boolean(editingRow)}
        readOnly={readOnly}
        renderField={renderField}
        row={editingRow}
        saving={saving}
        savingDisabled={masterLoading || !master}
        title="課題管理レコード詳細"
      />
      {saveError && (
        <div className="db-error-modal-overlay" onClick={() => setSaveError('')} role="presentation">
          <div className="db-error-modal" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <div className="db-error-modal-icon">!</div>
            <p className="db-error-modal-message">{saveError}</p>
            <button className="db-error-modal-button" onClick={() => setSaveError('')} type="button">
              OK
            </button>
          </div>
        </div>
      )}
    </>
  );
}
